// RecipeReviseSheet — "tell the kitchen what went wrong, get your
// version back." The AI text-prompt revision surface for saved
// recipes: "too spicy — take the heat down", "cake didn't rise —
// fix the leavening", "simplify step 3".
//
// Three phases:
//   compose   — textarea (optionally seeded from post-cook notes) +
//               example chips + the explicit REVISE WITH AI button.
//               PAID AI: this tap is the ONLY thing that fires the
//               model — opening the sheet costs nothing.
//   streaming — Claude rewrites the recipe live; we show the sticky
//               title + rough progress counts from a best-effort
//               partial parse of the accumulating JSON.
//   review    — the consent gate. A field-level diff (recipeDiff)
//               shows exactly what changed; TRY AGAIN returns to
//               compose with the feedback preserved; the save CTA
//               follows the smart-hybrid rule (own recipe → UPDATE
//               MY RECIPE in place; anything else → SAVE MY VERSION,
//               forkedFrom + tweak counts stamped).
//
// Saving routes through the SAME pipeline as manual edits
// (src/lib/recipeEdits.js) so technique re-validation and timer
// inference apply to AI output exactly like hand edits.

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR } from "../lib/tokens";
import { coerceRecipeCanonicalIds } from "../data/ingredients";
import { reviseRecipeStreaming } from "../lib/generateRecipe";
import { finalizeRecipeEdits, decideSaveMode, buildForkPayload } from "../lib/recipeEdits";
import { describeDiff } from "../lib/recipeDiff";

const EXAMPLES = [
  "Too spicy — take the heat down",
  "Came out dry — fix the cook time",
  "Simplify the steps for a weeknight",
];

// Best-effort partial parse of a streaming JSON recipe — enough for
// "Rewriting {title}… · N ingredients · M steps so far". Never
// authoritative; the complete event carries the real payload.
function peekStreamProgress(text) {
  const out = { title: null, ingredients: 0, steps: 0 };
  const t = String(text || "");
  const titleMatch = t.match(/"title"\s*:\s*"([^"]{1,120})"/);
  if (titleMatch) out.title = titleMatch[1];
  out.ingredients = (t.match(/"item"\s*:/g) || []).length;
  out.steps = (t.match(/"instruction"\s*:/g) || []).length;
  return out;
}

export default function RecipeReviseSheet({
  open,
  onClose,
  baseRecipe,          // full recipe JSON being revised
  bookRow = null,      // user_recipes row when this recipe lives in the book
  userId,
  seedText = "",       // post-cook notes prefill
  onSaveInPlace,       // (id, nextRecipe) => Promise
  onForkSave,          // (payload, source) => Promise
  onSaved = null,      // ({ mode }) => void
}) {
  const [phase, setPhase] = useState("compose"); // compose | streaming | review
  const [feedback, setFeedback] = useState(seedText || "");
  const [streamed, setStreamed] = useState("");
  const [result, setResult] = useState(null);    // { recipe, diff }
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [forkName, setForkName] = useState("");
  const runningRef = useRef(false);

  const saveMode = useMemo(
    () => decideSaveMode({ bookRow, viewerId: userId }),
    [bookRow, userId],
  );

  // Reset whenever the sheet opens for a (possibly different) recipe.
  useEffect(() => {
    if (!open) return;
    setPhase("compose");
    setFeedback(seedText || "");
    setStreamed("");
    setResult(null);
    setError(null);
    setSaving(false);
    setForkName(`My ${baseRecipe?.title || "Recipe"}`);
  }, [open, baseRecipe, seedText]);

  if (!open || !baseRecipe) return null;

  const runRevision = async () => {
    const text = feedback.trim();
    if (!text || runningRef.current) return;
    runningRef.current = true;
    setPhase("streaming");
    setStreamed("");
    setError(null);
    try {
      const { recipe: drafted } = await reviseRecipeStreaming({
        recipe: baseRecipe,
        feedback: text,
        onChunk: (chunk) => setStreamed((prev) => prev + chunk),
      });
      const coerced = coerceRecipeCanonicalIds(drafted);
      const { recipe: finalRecipe, diff } = finalizeRecipeEdits(coerced, baseRecipe);
      setResult({ recipe: finalRecipe, diff });
      setPhase("review");
    } catch (e) {
      setError(e?.message || "Revision failed — try again?");
      setPhase("compose");
    } finally {
      runningRef.current = false;
    }
  };

  const handleSave = async () => {
    if (!result || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (saveMode === "inPlace") {
        await onSaveInPlace?.(bookRow.id, result.recipe);
      } else {
        const payload = buildForkPayload(result.recipe, bookRow || { recipe: baseRecipe }, {
          title: forkName,
          diff: result.diff,
        });
        await onForkSave?.(payload, "custom");
      }
      onSaved?.({ mode: saveMode });
      onClose?.();
    } catch (e) {
      setError(e?.message || "Couldn't save — try again?");
      setSaving(false);
    }
  };

  const progress = phase === "streaming" ? peekStreamProgress(streamed) : null;
  const diffLines = result ? describeDiff(result.diff, { max: 10 }) : [];

  const kicker = {
    fontFamily: FONT.mono, fontSize: 12, color: COLOR.gold,
    letterSpacing: "0.15em",
  };

  return createPortal(
    <div
      onClick={() => { if (phase !== "streaming" && !saving) onClose?.(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 340,
        background: "rgba(0,0,0,0.72)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480, maxHeight: "88%",
          overflowY: "auto", WebkitOverflowScrolling: "touch",
          // SOLID opaque surface. COLOR.deep is rgba(255,255,255,0.42)
          // — a frosted-glass UNDERLAY meant to sit over a dark page
          // with a backdrop blur, NOT a modal card base. Used here it
          // let the recipe page bleed straight through. A solid warm
          // paper + shadow reads as an elevated sheet above the book.
          background: "#FBF7EF",
          borderTop: `1px solid ${COLOR.border}`,
          boxShadow: "0 -10px 36px rgba(40,28,12,0.30)",
          borderTopLeftRadius: 18, borderTopRightRadius: 18,
          padding: "20px 20px 18px", boxSizing: "border-box",
        }}
      >
        <div style={{ width: 42, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 14px" }} />

        {phase === "compose" && (
          <>
            <div style={kicker}>REVISE THIS RECIPE</div>
            <h2 style={{ fontFamily: FONT.serif, fontSize: 24, fontWeight: 300, fontStyle: "italic", color: COLOR.ink, margin: "6px 0 4px" }}>
              {baseRecipe.title}
            </h2>
            <p style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.muted, margin: "0 0 14px", lineHeight: 1.5 }}>
              Tell the kitchen what to change — taste, texture, timing,
              technique. You'll review every change before it saves.
            </p>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={'e.g. "too spicy — take the heat down" · "didn\'t rise — fix the leavening"'}
              rows={3}
              style={{
                width: "100%", boxSizing: "border-box",
                padding: "12px 14px",
                background: COLOR.ground, border: `1px solid ${COLOR.border}`,
                borderRadius: 12, fontFamily: FONT.sans, fontSize: 14,
                color: COLOR.ink, outline: "none", resize: "none",
                lineHeight: 1.5,
              }}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  className="mcm-focusable"
                  onClick={() => setFeedback(ex)}
                  style={{
                    padding: "6px 11px", borderRadius: 999,
                    background: COLOR.soil, border: `1px solid ${COLOR.border}`,
                    fontFamily: FONT.mono, fontSize: 12, color: COLOR.dim,
                    letterSpacing: "0.03em", cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  {ex}
                </button>
              ))}
            </div>
            {error && (
              <div style={{ marginTop: 10, padding: "9px 11px", background: COLOR.roseDeep, border: `1px solid ${COLOR.roseDim}`, borderRadius: 10, fontFamily: FONT.sans, fontSize: 12, color: COLOR.rose }}>
                {error}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                type="button"
                className="mcm-focusable"
                onClick={onClose}
                style={{ flex: 1, padding: "13px", background: COLOR.soil, border: `1px solid ${COLOR.border}`, borderRadius: 12, fontFamily: FONT.mono, fontSize: 12, color: COLOR.dim, letterSpacing: "0.08em", cursor: "pointer", touchAction: "manipulation" }}
              >
                CANCEL
              </button>
              <button
                type="button"
                className="mcm-focusable"
                onClick={runRevision}
                disabled={!feedback.trim()}
                style={{
                  flex: 2, padding: "13px", borderRadius: 12, border: "none",
                  background: feedback.trim() ? COLOR.gold : COLOR.soil,
                  color: feedback.trim() ? COLOR.night : COLOR.sub,
                  fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
                  letterSpacing: "0.08em",
                  cursor: feedback.trim() ? "pointer" : "not-allowed",
                  touchAction: "manipulation",
                }}
              >
                REVISE WITH AI →
              </button>
            </div>
            <div style={{ marginTop: 8, textAlign: "center", fontFamily: FONT.mono, fontSize: 12, color: COLOR.sub, letterSpacing: "0.06em" }}>
              USES AI · TAKES ~30 SECONDS
            </div>
          </>
        )}

        {phase === "streaming" && (
          <div style={{ padding: "26px 4px 20px", textAlign: "center" }}>
            <div style={kicker}>REWRITING</div>
            <h2 style={{ fontFamily: FONT.serif, fontSize: 22, fontWeight: 300, fontStyle: "italic", color: COLOR.ink, margin: "8px 0 6px" }}>
              {progress?.title || baseRecipe.title}
            </h2>
            <div style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted, letterSpacing: "0.06em" }}>
              {progress && (progress.ingredients > 0 || progress.steps > 0)
                ? `${progress.ingredients} INGREDIENTS · ${progress.steps} STEPS SO FAR`
                : "THINKING THROUGH YOUR FEEDBACK…"}
            </div>
            <div style={{
              margin: "18px auto 0", width: 26, height: 26,
              border: `3px solid ${COLOR.border}`, borderTopColor: COLOR.gold,
              borderRadius: "50%", animation: "reviseSpin 0.9s linear infinite",
            }} />
            <style>{`@keyframes reviseSpin { from { transform: rotate(0) } to { transform: rotate(360deg) } }`}</style>
          </div>
        )}

        {phase === "review" && result && (
          <>
            <div style={kicker}>REVIEW YOUR CHANGES</div>
            <h2 style={{ fontFamily: FONT.serif, fontSize: 22, fontWeight: 300, fontStyle: "italic", color: COLOR.ink, margin: "6px 0 4px" }}>
              {result.recipe.title || baseRecipe.title}
            </h2>
            <p style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, margin: "0 0 12px" }}>
              Based on: "{feedback.trim()}"
            </p>
            {diffLines.length > 0 ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {diffLines.map((line, i) => (
                  <div key={i} style={{
                    padding: "9px 12px",
                    background: COLOR.ground, border: `1px solid ${COLOR.border}`,
                    borderRadius: 10, fontFamily: FONT.sans, fontSize: 13,
                    color: line.startsWith("−") ? COLOR.rose
                      : line.startsWith("+") ? COLOR.leaf
                      : COLOR.ink,
                  }}>
                    {line}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: "12px", fontFamily: FONT.sans, fontSize: 13, color: COLOR.muted, fontStyle: "italic" }}>
                The revision came back nearly identical — try more specific
                feedback, or save anyway if the prose reads better.
              </div>
            )}

            {saveMode === "fork" && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.muted, letterSpacing: "0.1em", marginBottom: 6 }}>
                  YOUR VERSION'S NAME
                </div>
                <input
                  value={forkName}
                  onChange={(e) => setForkName(e.target.value)}
                  style={{
                    width: "100%", boxSizing: "border-box", padding: "11px 13px",
                    background: COLOR.ground, border: `1px solid ${COLOR.border}`,
                    borderRadius: 10, fontFamily: FONT.serif, fontStyle: "italic",
                    fontSize: 15, color: COLOR.ink, outline: "none",
                  }}
                />
              </div>
            )}

            {error && (
              <div style={{ marginTop: 10, padding: "9px 11px", background: COLOR.roseDeep, border: `1px solid ${COLOR.roseDim}`, borderRadius: 10, fontFamily: FONT.sans, fontSize: 12, color: COLOR.rose }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button
                type="button"
                className="mcm-focusable"
                onClick={() => { setPhase("compose"); setResult(null); }}
                disabled={saving}
                style={{ flex: 1, padding: "13px", background: COLOR.soil, border: `1px solid ${COLOR.border}`, borderRadius: 12, fontFamily: FONT.mono, fontSize: 12, color: COLOR.dim, letterSpacing: "0.06em", cursor: saving ? "default" : "pointer", touchAction: "manipulation" }}
              >
                TRY AGAIN
              </button>
              <button
                type="button"
                className="mcm-focusable"
                onClick={onClose}
                disabled={saving}
                style={{ flex: 1, padding: "13px", background: "transparent", border: `1px solid ${COLOR.border}`, borderRadius: 12, fontFamily: FONT.mono, fontSize: 12, color: COLOR.sub, letterSpacing: "0.06em", cursor: saving ? "default" : "pointer", touchAction: "manipulation" }}
              >
                DISCARD
              </button>
              <button
                type="button"
                className="mcm-focusable"
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 2, padding: "13px", borderRadius: 12, border: "none",
                  background: saving ? COLOR.soil : COLOR.leaf,
                  color: saving ? COLOR.sub : COLOR.night,
                  fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
                  letterSpacing: "0.06em",
                  cursor: saving ? "wait" : "pointer",
                  touchAction: "manipulation",
                }}
              >
                {saving ? "SAVING…" : saveMode === "inPlace" ? "UPDATE MY RECIPE" : "SAVE MY VERSION"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
